import { handleActions } from 'redux-actions';
import actions from '../actionTypes';
import {
    registerEvents,
    unRegisterEvents,
} from '../config/events';
import nextTick from '../utils/next-tick';

import { getActiveSlideInstance } from '../../../../js/common/anime.es.js';

import { emptyFunc } from '@play-utils/func';
import TimeRecord from '@src/slide/infrastructure/module/independent/time-record';
import { TDWActionType } from '../../../../slide/infrastructure/module/dependent/report/offline-data-report/constant';
import FPSRecord from '@src/slide/infrastructure/module/independent/performance/fps-record';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import isEqual from 'lodash/isEqual';
import has from 'lodash/has';
import { isSpecialActivity } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';

// 禁止向上向下翻页: 1、远程演示主讲人未允许观众翻页 2、其他情况，比如某些运营活动
const forbidFlipPage = s => (s.remotePlayRole === AUDIENCE && !s.allowFilpPage) || isSpecialActivity;

// 禁止跳转至结束页
const forbidJumpEndPage = s => s.currentPage === s.pageCount - 1 && isSpecialActivity;

const {
    START_PLAY,
    END_PLAY,
    NEXT_PAGE,
    PREV_PAGE,
    NEXT_PAGE_WITHOUT_FRAGMENT,
    PREV_PAGE_WITHOUT_FRAGMENT,
    JUMP_PAGE,
    SHOW_DIALOG,
    HIDE_DIALOG,
    TURN_ON_LASER,
    TURN_OFF_LASER,
    TURN_ANNOTATION,
    BE_MASTER,
    BE_AUDIENCE,
    END_REMOTE_PLAY,
    // MASTER_PEN_UPDATED,
    // SENT_PEN_DATA,
    AUDIENCE_PEN_DRAWING,
    SLIDE_SCALE_CHANGED,
    NEXT_PAGE_REMOTE,
    PREV_PAGE_REMOTE,
    JUMP_PAGE_REMOTE,
    ALLOW_FLIP_PAGE,
    FORIBD_FLIP_PAGE,
    SYCN_WITH_MASTER,
    MASTER,
    AUDIENCE,
    INIT_CLIENT_WIDTHHEIGHT,
    INTERACTIVE_ANIM_NEXT,
    INTERACTIVE_ANIM_RESET,
    STOP_MAIN_ANIM,
    HANDLE_FIRST_ANIM_WITH_EFFECT,
    START_TRANSITION,
    STOP_TRANSITION,
    SHOULD_STOP_TRANSITION,
} = actions;

/**
 * to page x fragment x 的通用逻辑
 * @param {object} Object
 * @param {number} Object.page 跳转到的页面
 * @param {number} Object.fragment 跳转到的碎片
 * @param {boolean} withFragment 是否保留动画 fragment，默认 true；
 * @returns {object} 挂到state上的新object
 */
function jumpTo({
    page, fragment, pageCount, history, direction, iaFragmentInfo = {},
    shouldStopMainAnim = false,
    isLoadPageBySwitchForward = false,
}: {
    fragment?: number;
    direction?: number;
    page: number;
    pageCount: number;
    history: any;
    iaFragmentInfo?: any;
    shouldStopMainAnim?: boolean;
    isLoadPageBySwitchForward?: boolean;
}, withFragment = true) {
    nextTick(() => {
        // 因为是直接改store，所以必须要放在下一个loop里做
        history?.replace(`/${page}`);
    });

    return {
        pageCount,
        currentPage: page,
        currentFragment: withFragment ? fragment : 0,
        transitionDirection: direction,
        iaFragmentInfo: withFragment ? iaFragmentInfo : {},
        shouldStopMainAnim,
        isLoadPageBySwitchForward,
    };
}

function nextPagelocal(s) {
    if (forbidJumpEndPage(s)) return s;
    if (s.currentPage === s.pageCount) {
        if (s.remotePlayRole === AUDIENCE) {
            return s;
        }
        const result = { shouldEndPlay: true };
        return { ...s, ...result };
    }

    if (s.currentPage !== s.pageCount - 1) {
        TimeRecord.start(TDWActionType.PLAYER_SWITCH, { from: s.currentPage, to: s.currentPage + 1 });
    }
    const page = s.getSiblingPageIndex(s.currentPage);
    // 记录切页耗时起始时间
    const result: any = jumpTo({
        page,
        fragment: 0,
        pageCount: s.pageCount,
        history: s.history,
        direction: 1,
        shouldStopMainAnim: false,
        isLoadPageBySwitchForward: false,
    });
    tdwPlayerAnimFps(s);

    if (s.remotePlayRole === AUDIENCE && s.allowFilpPage) {
        // 远程播放的观众，手动翻页后的标志位。
        result.isManulFlipped = (result.currentPage !== s.remoteCurrentPage)
        || (result.currentFragment !== s.remoteCurrentFragment);
    }
    return result;
}

function tdwPlayerAnimFps(s) {
    const animCount = FPSRecord.animeCount;
    const fps = FPSRecord.fps();
    if (fps > 0) {
        const data: any = {};
        data.slideIndex = s.currentPage;
        data.elementCount = FPSRecord.elementCount;
        data.animCount = animCount;
        data.avg_fps = fps;
        // window && window.log && window.log.info('~_~ avg_fps', fps);
        const info = JSON.stringify(data);
        tdw({
            opername: 'quality_slide',
            module: 'player',
            action: TDWActionType.PLAYER_ANIM_FPS,
            ver6: info,
        });
    }
}

function prevPageLocal(s) {
    if (s.currentPage === 0) return s;

    // 记录切页耗时起始时间
    TimeRecord.start(TDWActionType.PLAYER_SWITCH, { from: s.currentPage, to: s.currentPage - 1 });
    const page = s.getSiblingPageIndex(s.currentPage, true);
    const prevPage = s.currentPage - 1;
    const firstAnimIsWithEffect = s.firstAnimIsWithEffectInfos[prevPage];
    const mainSeqAnimNum =  s.fragmentLengthInfos[prevPage];
    const result: any = jumpTo({
        page,
        fragment: firstAnimIsWithEffect ? mainSeqAnimNum - 1 : mainSeqAnimNum,
        pageCount: s.pageCount,
        history: s.history,
        direction: 0,
        shouldStopMainAnim: false,
        isLoadPageBySwitchForward: true,
    });

    if (s.remotePlayRole === AUDIENCE && s.allowFilpPage) {
        // 远程播放的观众，手动翻页后的标志位。
        result.isManulFlipped = (result.currentPage !== s.remoteCurrentPage)
        || (result.currentFragment !== s.remoteCurrentFragment);
    }

    return result;
}

// TODO 待删除，目前播放页的默认淡入淡出的切页动画还用到了 anime，后续真正的切页动画实现之后这部分逻辑就可以删除
// src/framework/player/src/components/slide-wrapper.tsx initEnterAnim
function cancelSlideAnims() {
    const activeSlideInstances = getActiveSlideInstance();
    if (activeSlideInstances && activeSlideInstances.length > 0) {
        let activeSlideInstancesLength = activeSlideInstances.length;
        let j = activeSlideInstancesLength - 1;
        while (j >= 0) {
            const activeSlideInstance = activeSlideInstances[j];
            activeSlideInstance.pause();
            if (activeSlideInstance.finalStyle) {
                activeSlideInstance.set(activeSlideInstance.targets, activeSlideInstance.finalStyle);
            }
            if (activeSlideInstance.complete) {
                activeSlideInstance.complete(activeSlideInstance);
            }
            const instanceSlideIndex = activeSlideInstances.indexOf(activeSlideInstance);
            if (instanceSlideIndex > -1) {
                activeSlideInstances.splice(instanceSlideIndex, 1);
                activeSlideInstancesLength = activeSlideInstances.length;
            }
            j -= 1;
        }
    }
}

const initial = ({
    currentPage = 0,
    currentFragment = 0,
    pageCount = 0,
    history = null,
    handleEndPlay = emptyFunc,
    shouldPlayerShortcutActive = () => true,
    allowFilpPage = true, // 远程播放时是否允许观众翻页。
    isManulFlipped = false, // 观众是否已经手动翻页。
    remotePlayRole = '', // 远程播放角色。
    remoteCurrentPage = 0,
    remoteCurrentFragment = 0,
    shouldEndPlay = false,
    laserTurnOn = false,
    enableAnnotation = false,
    annotationInitData = null,
    fragmentLengthInfos = [],
    firstAnimIsWithEffectInfos = [],
    transitionDirection = 1, // 动画方向
    iaFragmentInfo = {},
    iaFragmentLengthInfos = {},
    remoteIaFragmentInfo = {},
    shouldStopMainAnim = false,
    firstAnimIsWithEffect = false,
    getSiblingPageIndex = emptyFunc,
    isSupportTransition = false,
    isTransitionActive = false,
    shouldStopTransition = false,
    isLoadPageBySwitchForward = false, // false 为向后切页， true 为向前翻页
} = {}) => ({
    handleEndPlay,
    shouldPlayerShortcutActive,
    getSiblingPageIndex,
    history,
    currentPage,
    currentFragment,
    pageCount,
    allowFilpPage,
    isManulFlipped,
    remotePlayRole,
    remoteCurrentPage,
    remoteCurrentFragment,
    shouldEndPlay,
    laserTurnOn,
    enableAnnotation,
    annotationInitData,
    fragmentLengthInfos,
    firstAnimIsWithEffectInfos,
    transitionDirection,
    iaFragmentInfo,
    iaFragmentLengthInfos,
    remoteIaFragmentInfo,
    shouldStopMainAnim,
    firstAnimIsWithEffect,
    isSupportTransition,
    isTransitionActive,
    shouldStopTransition,
    isLoadPageBySwitchForward,
});

const reducer = handleActions(
    {
        [INIT_CLIENT_WIDTHHEIGHT]: (s, a: { payload: { clientWidth: number; clientHeight: number } }) => {
            if (!a.payload?.clientWidth || !a.payload.clientHeight) {
                a.payload = {
                    clientWidth: 0,
                    clientHeight: 0,
                };
            }
            return { ...s, ...a.payload };
        },
        [START_PLAY]: (s, a) => {
            // TODO 修改router
            registerEvents(a.payload.escBtnEnable, a.payload.shouldPlayerShortcutActive);
            return initial(a.payload);
        },
        [END_PLAY]: (s) => {
            if (s.remotePlayRole === AUDIENCE) {
                // 远程播放的观众，不能主动退出。
                return s;
            }

            unRegisterEvents();
            let { currentPage } = s;
            nextTick(() => {
                // 结束页是尾页 index+1, endplay 时要还原回尾页的页数
                if (currentPage === s.pageCount) currentPage -= 1;
                s.handleEndPlay(currentPage);
            });

            // TODO 清空router
            return initial();
        },
        [SHOW_DIALOG]: (s) => {
            unRegisterEvents();
            const temp = {
                showDialog: true,
            };
            return { ...s, ...temp };
        },
        [HIDE_DIALOG]: (s) => {
            registerEvents(true, s.shouldPlayerShortcutActive);
            const temp = {
                showDialog: false,
            };
            return { ...s, ...temp };
        },
        [HANDLE_FIRST_ANIM_WITH_EFFECT]: (s, a: { payload: { firstAnimIsWithEffect: boolean } }) => ({
            ...s,
            firstAnimIsWithEffect: a.payload.firstAnimIsWithEffect,
        }),
        [NEXT_PAGE_WITHOUT_FRAGMENT]: (s) => {
            if (forbidFlipPage(s)) return s;

            const result = nextPagelocal(s);
            cancelSlideAnims();
            return { ...s, ...result };
        },
        // eslint-disable-next-line complexity
        [NEXT_PAGE]: (s) => {
            if (forbidFlipPage(s)) return s;

            if (s.isTransitionActive) {
                return {
                    ...s,
                    isTransitionActive: false,
                    shouldStopTransition: true,
                };
            }

            if (s.currentFragment < s.fragmentLengthInfos[s.currentPage]) {
                const result: any = {
                    currentFragment: s.currentFragment + 1,
                    transitionDirection: 1,
                };
                if (s.remotePlayRole === AUDIENCE && s.allowFilpPage) {
                    // 远程播放的观众，手动翻页后的标志位。
                    result.isManulFlipped = (s.currentPage !== s.remoteCurrentPage)
                    || (result.currentFragment !== s.remoteCurrentFragment)
                    || !(isEqual(s.iaFragmentInfo, s.remoteIaFragmentInfo));
                }

                // 当前页有order为0的动画，且最后一组主队列动画已播完时，直接切到下一页
                if (s.currentFragment === s.fragmentLengthInfos[s.currentPage] - 1 && s.firstAnimIsWithEffect) {
                    return { ...s, ...nextPagelocal(s) };
                }
                return { ...s, ...result };
            }
            cancelSlideAnims();

            const result = nextPagelocal(s);

            return { ...s, ...result };
        },
        [PREV_PAGE_WITHOUT_FRAGMENT]: (s) => {
            if (forbidFlipPage(s)) return s;

            const result = prevPageLocal(s);
            cancelSlideAnims();
            return { ...s, ...result };
        },
        [PREV_PAGE]: (s) => {
            if (forbidFlipPage(s)) return s;

            if (s.currentFragment > 0) {
                const result: any = {
                    currentFragment: s.currentFragment - 1,
                    transitionDirection: 0,
                };
                if (s.remotePlayRole === AUDIENCE && s.allowFilpPage) {
                    // 远程播放的观众，手动翻页后的标志位。
                    result.isManulFlipped = (s.currentPage !== s.remoteCurrentPage)
                    || (result.currentFragment !== s.remoteCurrentFragment)
                    || !(isEqual(s.iaFragmentInfo, s.remoteIaFragmentInfo));
                }
                return { ...s, ...result };
            }

            if (s.firstAnimIsWithEffect && s.currentFragment === 0) {
                //  currentFragment 为0， 且为立即播放动画
                return { ...s, currentFragment: -1 };
            }

            const result = prevPageLocal(s);
            cancelSlideAnims();

            return { ...s, ...result };
        },
        // eslint-disable-next-line complexity
        [JUMP_PAGE]: (s, a: { payload: number }) => {
            if (a.payload < 0 || a.payload >= s.pageCount) return s;
            if (a.payload === s.currentPage) return s;

            if (s.remotePlayRole === AUDIENCE && !s.allowFilpPage) {
                // 远程播放的观众，只有在主持人允许的时才能翻页。
                return s;
            }
            cancelSlideAnims();

            // 跳转到页面不应该保留动画帧
            const result: any = jumpTo({
                page: a.payload,
                pageCount: s.pageCount,
                history: s.history,
                shouldStopMainAnim: false,
                isLoadPageBySwitchForward: false,
            }, false);

            if (s.remotePlayRole === AUDIENCE && s.allowFilpPage) {
                // 远程播放的观众，手动翻页后的标志位。
                result.isManulFlipped = (result.currentPage !== s.remoteCurrentPage)
                || (result.currentFragment !== s.remoteCurrentFragment);
            }

            return { ...s, ...result };
        },
        [TURN_ON_LASER]: (s) => {
            const event = new Event('laserTurnOn');
            setTimeout(() => {
                window.dispatchEvent(event);
            }, 0);
            return { ...s, laserTurnOn: true };
        },
        [TURN_OFF_LASER]: (s) => {
            const event = new Event('laserTurnOff');
            setTimeout(() => {
                window.dispatchEvent(event);
            }, 0);
            return { ...s, laserTurnOn: false };
        },
        [TURN_ANNOTATION]: (s, a: { payload: { enable: boolean; initData: any } }) => ({
            ...s,
            enableAnnotation: a.payload.enable,
            annotationInitData: a.payload.initData,
        }),
        [BE_MASTER]: s => ({ ...s, remotePlayRole: MASTER }),
        [BE_AUDIENCE]: s => ({ ...s, remotePlayRole: AUDIENCE }),
        [END_REMOTE_PLAY]: s => ({ ...s, remotePlayRole: '' }),

        [AUDIENCE_PEN_DRAWING]: (s) => {
            const rightQ = Array.isArray(s.receivePenQ) ? s.receivePenQ : [];
            const pen = rightQ.shift() || null;

            return {
                ...s, dawingPen: pen,
                receivePenQ: [rightQ],
            };
        },
        [SLIDE_SCALE_CHANGED]: (s, a: { payload: number }) => ({ ...s, scale: a.payload }),

        [NEXT_PAGE_REMOTE]: (s) => {
            if (s.remotePlayRole === AUDIENCE && s.allowFilpPage && s.isManulFlipped) {
                if (s.remoteCurrentPage === s.pageCount - 1) return s;
                // 允许观众翻页，并且用户已经不在当前页了，那就不真正翻页。
                const remotePage = s.remoteCurrentPage + 1;
                return {
                    ...s, remoteCurrentPage: remotePage,
                    remoteCurrentFragment: s.remoteCurrentFragment,
                    shouldStopMainAnim: false,
                    isManulFlipped: (s.currentPage !== remotePage) || (s.currentFragment !== s.remoteCurrentFragment)
                        || !isEqual(s.iaFragmentInfo, s.remoteIaFragmentInfo),
                };
            }

            if (s.currentPage === s.pageCount) return s;
            TimeRecord.start(TDWActionType.PLAYER_SWITCH, { from: s.currentPage, to: s.currentPage + 1 });
            const result: any = jumpTo({
                page: s.currentPage + 1,
                fragment: s.currentFragment,
                pageCount: s.pageCount,
                history: s.history,
                shouldStopMainAnim: false,
                isLoadPageBySwitchForward: false,
            });
            result.remoteCurrentPage = result.currentPage;
            result.remoteCurrentFragment = result.currentFragment;
            result.remoteIaFragmentInfo = result.iaFragmentInfo;
            result.isManulFlipped = false;
            return { ...s, ...result };
        },
        [PREV_PAGE_REMOTE]: (s) => {
            if (s.remotePlayRole === AUDIENCE && s.allowFilpPage && s.isManulFlipped) {
                if (s.remoteCurrentPage === 0) return s;
                // 允许观众翻页，并且用户已经不在当前页了，那就不真正翻页。
                const remotePage = s.remoteCurrentPage - 1;
                const temp = {
                    remoteCurrentPage: remotePage,
                    remoteCurrentFragment: s.remoteCurrentFragment,
                    shouldStopMainAnim: false,
                    isManulFlipped: (s.currentPage !== remotePage) || (s.currentFragment !== s.remoteCurrentFragment)
                        || !isEqual(s.iaFragmentInfo, s.remoteIaFragmentInfo),
                };
                return { ...s, ...temp };
            }

            if (s.currentPage === 0) return s;
            TimeRecord.start(TDWActionType.PLAYER_SWITCH, { from: s.currentPage, to: s.currentPage - 1 });
            const result: any = jumpTo({
                page: s.currentPage - 1,
                fragment: s.currentFragment,
                pageCount: s.pageCount,
                history: s.history,
                shouldStopMainAnim: false,
                isLoadPageBySwitchForward: true,
            });
            result.remoteCurrentPage = result.currentPage;
            result.remoteCurrentFragment = result.currentFragment;
            result.remoteIaFragmentInfo = result.iaFragmentInfo;
            result.isManulFlipped = false;
            return { ...s, ...result };
        },
        // eslint-disable-next-line complexity
        [JUMP_PAGE_REMOTE]: (s, a) => {
            if (a.payload.p < 0 || a.payload.p > s.pageCount) return s;

            if (s.remotePlayRole === AUDIENCE && s.allowFilpPage && s.isManulFlipped) {
                // 允许观众翻页，并且用户已经不在当前页了，那就不真正翻页。
                const remotePage = a.payload.p;
                const remoteFragment = a.payload.f;
                const remoteIaFragmentInfo = a.payload.iaF;
                const temp: any = {
                    remoteCurrentPage: remotePage,
                    remoteCurrentFragment: remoteFragment,
                    remoteIaFragmentInfo,
                    shouldStopMainAnim: a.payload.stopA,
                    isManulFlipped: (s.currentPage !== remotePage) || (s.currentFragment !== remoteFragment)
                        || !isEqual(s.iaFragmentInfo, remoteIaFragmentInfo),
                };
                return { ...s, ...temp };
            }
            TimeRecord.start(TDWActionType.PLAYER_SWITCH, { from: s.currentPage, to: a.payload });

            let direction = 1;
            if (a.payload.p < s.currentPage) {
                direction = 0;
            } else if (a.payload.p === s.currentPage) {
                if (a.payload.f > s.currentFragment) {
                    direction = 1;
                } else if (a.payload.f < s.currentFragment) {
                    direction = 0;
                } else {
                    direction = s.transitionDirection;
                }
            } else {
                direction = 1;
            }

            // 如果演讲者停止了当前的Transition(切页动画)，并且是同一个页面正在进行切页动画，则也停止当前的transition
            if (a.payload.stopT && s.isTransitionActive && s.currentPage === a.payload.p) {
                return {
                    ...s,
                    isTransitionActive: false,
                    shouldStopTransition: true,
                };
            }

            const result: any = jumpTo({
                page: a.payload.p,
                fragment: a.payload.f,
                pageCount: s.pageCount,
                history: s.history,
                direction,
                iaFragmentInfo: a.payload.iaF,
                shouldStopMainAnim: a.payload.stopA,
                isLoadPageBySwitchForward: a.payload.prev,
            });
            result.remoteCurrentPage = result.currentPage;
            result.remoteCurrentFragment = result.currentFragment;
            result.remoteIaFragmentInfo = result.iaFragmentInfo;
            result.isManulFlipped = false;
            return { ...s, ...result };
        },
        [ALLOW_FLIP_PAGE]: (s) => {
            const temp = {
                allowFilpPage: true, // 远程播放时是否允许观众翻页。
            };
            return { ...s, ...temp };
        },
        [FORIBD_FLIP_PAGE]: (s) => {
            const temp = {
                allowFilpPage: false, // 远程播放时是否允许观众翻页。
            };
            return { ...s, ...temp };
        },
        [SYCN_WITH_MASTER]: (s) => {
            const temp = {
                isManulFlipped: false, // 与演讲者同步进度
            };
            return { ...s, ...temp };
        },
        [INTERACTIVE_ANIM_NEXT]: (s, a: { payload: { clickSpid: string } }) => {
            if (s.remotePlayRole === AUDIENCE) {
                // 远程播放不允许点击触发器。
                return s;
            }
            const iaFragmentInfo = { ...s.iaFragmentInfo };
            const { clickSpid } = a.payload;
            if (!has(iaFragmentInfo, clickSpid)) {
                iaFragmentInfo[clickSpid] = 0;
            }
            iaFragmentInfo[clickSpid] += 1;
            if (iaFragmentInfo[clickSpid] > s.iaFragmentLengthInfos[s.currentPage][clickSpid]) {
                iaFragmentInfo[clickSpid] = 1;
            }
            return {
                ...s,
                iaFragmentInfo,
            };
        },
        [INTERACTIVE_ANIM_RESET]: (s, a: { payload: { clickSpid: string } }) => {
            if (s.remotePlayRole === AUDIENCE) {
                // 远程播放不允许点击触发器。
                return s;
            }
            const iaFragmentInfo = { ...s.iaFragmentInfo };
            const { clickSpid } = a.payload;
            iaFragmentInfo[clickSpid] = 0;
            return {
                ...s,
                iaFragmentInfo,
            };
        },
        [STOP_MAIN_ANIM]: (s, a: {payload: { shouldStopMainAnim: boolean}}) => {
            const { shouldStopMainAnim } = a.payload;
            return ({ ...s, shouldStopMainAnim });
        },
        [SHOULD_STOP_TRANSITION]: s => ({ ...s, shouldStopTransition: false, isTransitionActive: false }),
        [START_TRANSITION]: s => ({ ...s, isTransitionActive: true }),
        [STOP_TRANSITION]: s => ({ ...s, isTransitionActive: false }),
    },
    initial(),
);

export default reducer;

// 单元测试
export const testable = {
    nextPagelocal,
    tdwPlayerAnimFps,
};
